import {
  Color,
  GridHelper,
  Material,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  PerspectiveCamera,
  Vector2,
  Vector3,
} from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { Engine, Entity, registerEvents } from "../engine/Engine";
import { getEventPixeslOnElement } from "../event/EventUtil";
import { SugarModel } from "../loader/SugarModel";
import { SugarModelConfigurator } from "../loader/SugarModelConfigurator";
import { SugarModelDimensions } from "../loader/SugarModelDimensions";
import {
  getModelObjectFirst,
  pickIntersectionForSceneSelection,
  raycast,
  raycastOnPlaneFromScreen,
} from "../physics/PhysicsUtil";
import { alignJoints, fetchMinDistance } from "../util/JointUtil";
import { fetchMaterialObjectMap } from "../util/ThreeUtil";
import {
  CONFIGURATOR_MODEL_TYPE,
  MODEL_PART_TYPE,
  getConfiguratorData,
} from "./ConfiguratorData";
import { SmoothOrbitController } from "./SmoothOrbitController";
import { logger } from "../util/Logger";

type MaterialConfigEntry = { code: string; material: any };

@registerEvents(
  "dblclick",
  "dragstart",
  "dragover",
  "drop",
  "pointerdown",
  "pointermove",
  "pointerup",
  "model-selected",
  "model-created",
  "showDimensions",
  "showTopView",
  "delete"
)
export class ConfiguratorController extends Entity {
  private _configurators: SugarModel[] = [];

  private _grabbed = false;
  private _mapMaterialObjects?: Map<Material, Object3D[]>;

  private _selectedModelPosition: Vector3 = new Vector3();
  private _selectedPlanePosition: Vector3 = new Vector3();
  private _selectedModel?: SugarModel;
  private _selectedOldConfigurator?: SugarModelConfigurator;
  private _mousedownModel?: SugarModel;
  private _orbitControls?: OrbitControls;
  private _smoothOrbitControls: SmoothOrbitController;
  private _mouseCoord = new Vector2(-1, -1);

  private _selectionModelFunction = (object: Object3D): boolean =>
    object instanceof SugarModel && !(object as SugarModel).freeze;

  private _selectionConfigFunction = (object: Object3D): boolean =>
    object instanceof SugarModelConfigurator;

  private _selectionFunction = this._selectionModelFunction;

  private _material: MeshStandardMaterial = new MeshStandardMaterial({
    transparent: true,
    opacity: 0.8,
    color: new Color(0.5, 0.5, 0.5),
    name: "checkMaterial",
  });

  private _gridHelper;

  private readonly _defaultPartConfig = new Map<string, MODEL_PART_TYPE>();
  private readonly _defaultSideConfig = new Map<string, MODEL_PART_TYPE>();
  private readonly _defaultMaterialConfig = new Map<string, MaterialConfigEntry>();

  private readonly _configuratorId: string;

  get configData(): CONFIGURATOR_MODEL_TYPE {
    return getConfiguratorData(this._configuratorId);
  }

  constructor(orbitControls: OrbitControls, configuratorId: string = "default") {
    super();
    this._orbitControls = orbitControls;
    this._configuratorId = configuratorId;
    this._smoothOrbitControls = new SmoothOrbitController(this._orbitControls);

    this._gridHelper = new GridHelper(
      10,
      100,
      new Color(0, 0, 0),
      new Color(0.5, 0.5, 0.5)
    );
    //this.engine?.activeScene?.add(this._gridHelper);
    this.name = "SugarConfiguratorControllerTarget";
  }

  onAdd(engine: Engine): void {
    this.engine?.addEntity(this._smoothOrbitControls);
  }

  onRemove(engine: Engine): void {
    this.engine?.removeEntity(this._smoothOrbitControls);
  }

  get models() {
    return this._configurators;
  }

  get configurators() {
    return this._configurators.map((m) => m as SugarModelConfigurator);
  }

  get isGrabbed() {
    return this._grabbed;
  }

  get selectedModel() {
    return this._selectedModel;
  }

  onModelCreatedOccured(e: any) {
    const newModel = e.detail;
    /* new SugarModelConfigurator();
    newModel.addModel(e.detail);*/
    this.engine?.activeScene?.add(newModel);

    this._mousedownModel = newModel;
    this.grabModel(this._mousedownModel!);
  }

  onDblclickOccured(event: MouseEvent | Touch) {
    const cast = this.castModel(event);
    const castPlane = this.castPlane(event);

    this._selectionFunction = this._selectionConfigFunction;

    if (cast) {
      this.selectedModel = getModelObjectFirst(
        cast.object,
        this._selectionFunction
      ) as SugarModelConfigurator;
      this.selectedModel.getWorldPosition(this._selectedModelPosition);
      this._selectedPlanePosition.copy(castPlane);
    }
  }

  onDragoverOccured(event: DragEvent) {
    event.preventDefault();
    this.onPointermoveOccured(event);
  }

  onDropOccured(event: DragEvent) {
    event.preventDefault();
    this.onPointerupOccured(event);
  }

  onPointerdownOccured(event: MouseEvent) {
    if (event.button > 0) return;

    event.preventDefault();
    this._mouseCoord = getEventPixeslOnElement(event);
    const cast = this.castModel(event);

    if (cast) {
      const castPlane = this.castPlane(event);
      this._mousedownModel = getModelObjectFirst(
        cast.object,
        this._selectionFunction
      ) as SugarModelConfigurator;

      this._mousedownModel.getWorldPosition(this._selectedModelPosition);
      this._selectedPlanePosition.copy(castPlane);

      this._orbitControls!.enabled = false;
    }
  }

  onPointermoveOccured(event: MouseEvent) {
    const mouseCoordNew = getEventPixeslOnElement(event);
    const mouseDistance = mouseCoordNew.distanceTo(this._mouseCoord);

    if (this._mousedownModel && mouseDistance > 2 && !this.isGrabbed) {
      this.grabModel(this._mousedownModel);
    }

    if (this._mousedownModel && this.isGrabbed) {
      const position = this.castPlane(event).sub(this._selectedPlanePosition);
      const newPosition = this._selectedModelPosition.clone().add(position);
      this.checkModel(this._mousedownModel!, newPosition);
    }
  }

  onPointerupOccured(event: MouseEvent) {
    this._orbitControls!.enabled = true;

    const mouseCoord = getEventPixeslOnElement(event);
    const mosueDist = mouseCoord.distanceTo(this._mouseCoord);

    //if it is grabbed, place somewhere
    if (this._mousedownModel && this.isGrabbed) {
      this.addModel(this._mousedownModel);
      this.fitCamera();
      this.engine!.dispatchCustomEvent("model-added");
    }

    //mark as select
    if (mosueDist < 5) this.selectedModel = this._mousedownModel;

    //release
    this._mousedownModel = undefined;
  }

  set selectedModel(value: SugarModel | undefined) {
    const oldValue = this._selectedModel;
    this._selectedModel = value;

    if (this._selectedModel && this._selectedModel != oldValue) {
      this.engine!.dispatchCustomEvent("model-selected", {
        detail: this._selectedModel,
        event: event,
      });
    } else if (oldValue && !this._selectedModel) {
      this.engine!.dispatchCustomEvent("model-deselected", {
        detail: this._selectedModel,
        event: event,
      });
      this._selectionFunction = this._selectionModelFunction;
    }
  }

  grabModel(checkModel: SugarModel) {
    this.setMaterials(checkModel);

    const configurator = checkModel.configurator as SugarModelConfigurator;
    if (configurator) {
      logger.info("Grab configurator", configurator);
      configurator.grabModel(checkModel);
      this._selectedOldConfigurator = configurator;
      checkModel.configurator = undefined;
    }

    if (configurator && configurator.models.length < 1) {
      configurator.dispose();
      this.__removeModel(configurator);
    }

    this.engine?.activeScene?.attach(checkModel);
    this._grabbed = true;
  }

  private _matchJointModel:
    | {
        distance: number;
        base: any;
        tail: any;
      }
    | undefined;

  checkModel(checkModel: SugarModel, position: Vector3 = new Vector3()) {
    checkModel.position.copy(position);
    this._matchJointModel = undefined;
    if (checkModel instanceof SugarModelConfigurator) return;

    if (this._configurators.length > 0) {
      //checkModel.rotation.set(0, 0, 0);
      checkModel.updateWorldMatrix(true, true);

      const minModel = fetchMinDistance(checkModel, this._configurators);
      if (minModel && minModel.distance < 1) {
        alignJoints(minModel!.base, minModel!.tail);
        this._matchJointModel = minModel;
      }
    }
  }

  addModel(addModel: SugarModel) {
    const match = this._matchJointModel;
    if (match) {
      const baseModel = match.base.model;

      if (baseModel.configurator) {
        (baseModel.configurator as SugarModelConfigurator).addModel(addModel);
        this.__removeModel(addModel);
      } else {
        this.__addModel(addModel);
      }
    } else {
      if (addModel instanceof SugarModelConfigurator) {
        logger.info(
          "SugarConfiguratorController: Model is configurator, it is added to configurator."
        );
        this.__addModel(addModel);
      } else if (addModel.data.replaceType == 0) {
        logger.warn(
          "SugarConfiguratorController: Model is not configured, but it is added to configurator."
        );
        const confg = new SugarModelConfigurator(
          new Map(
            this._selectedOldConfigurator?.mainConfig || this._defaultPartConfig
          ),
          new Map(
            this._selectedOldConfigurator?.sideConfig || this._defaultSideConfig
          ),
          new Map(
            this._selectedOldConfigurator?.materialConfig ||
              this._defaultMaterialConfig
          ),
          this.configData
        );
        if (this._selectedOldConfigurator?.models.length == 0)
          this._selectedOldConfigurator = undefined;
        confg.addEventListener("configured" as any, () => {
          this.engine?.dispatchCustomEvent("scene-changed");
          this._dimensionsController.update(this._configurators);
        });
        confg.addModel(addModel);
        this.__addModel(confg);
      } else {
        addModel.removeFromParent();
      }
    }

    this.resetMaterials(addModel);
    //
    this._selectedOldConfigurator?.complete();
    this._selectedOldConfigurator = undefined;
    //
    this._grabbed = false;
  }

  removeModel(removeModel: SugarModel) {
    this._configurators = this._configurators.filter((e) => e != removeModel);
    this.remove(removeModel);
    this.engine?.dispatchCustomEvent("scene-changed");
    this._dimensionsController.update(this._configurators);
  }

  removeSelected() {
    if (!this._selectedModel) return;
    if (this._selectedModel.configurator) {
      const sugarModelConf = this._selectedModel
        .configurator as SugarModelConfigurator;

      sugarModelConf.removeModel(this._selectedModel);
      this._selectedModel.removeFromParent();

      if (sugarModelConf.products.length < 1) this.removeModel(sugarModelConf);
    } else {
      this.removeModel(this._selectedModel);
    }

    this.fitCamera();
    this.selectedModel = undefined;
  }

  castModel(event: MouseEvent | Touch | TouchEvent) {
    if (1 > this.models.length) return;

    const casts = raycast(event, this.models).filter(
      (e) => !(e.object as any).isLine
    );
    return pickIntersectionForSceneSelection(casts);
  }

  castPlane(event: MouseEvent | Touch | TouchEvent) {
    return raycastOnPlaneFromScreen(
      event,
      {
        position: new Vector3(),
        normal: new Vector3(0, 1, 0),
      },
      Engine.activeEngine!.activeCamera!.instance,
      Engine.activeEngine.canvas
    );
  }

  removeAll() {
    this._configurators.forEach((m) => {
      this.remove(m);
    });
    this._configurators = [];
  }

  setMainPart(detail: MODEL_PART_TYPE) {
    this._defaultPartConfig.set(detail.type, detail);
  }

  async changeMainPart() {
    const selectedConfigurator = this.getSelectedConfigurator();
    return await selectedConfigurator?.changeMainPart(
      new Map(this._defaultPartConfig)
    );
  }

  setSidePart(config: MODEL_PART_TYPE) {
    this._defaultSideConfig.set(config.type, config);
  }

  async changeSidePart() {
    logger.debug("changeSidePart", this._defaultSideConfig);
    const selectedConfigurator = this.getSelectedConfigurator();
    logger.debug("selectedConfigurator", selectedConfigurator);
    await selectedConfigurator?.changeSidePart(new Map(this._defaultSideConfig));
    logger.debug("selectedConfigurator after changeSidePart", selectedConfigurator);
    this._dimensionsController.update([selectedConfigurator]);
    logger.debug("dimensionsController updated", this._dimensionsController);
  }

  setMaterial(materialConfig: Map<string, any>) {
    this._defaultMaterialConfig.clear();
    materialConfig
      .keys()
      .forEach((k) => this._defaultMaterialConfig.set(k, materialConfig.get(k)));
  }

  async applyMaterial() {
    const selectedConfigurator = this.getSelectedConfigurator();
    return await selectedConfigurator?.applyMaterial(
      new Map(this._defaultMaterialConfig)
    );
  }

  private getSelectedConfigurator() {
    if (this._selectedModel instanceof SugarModelConfigurator)
      return this._selectedModel;
    if (this._selectedModel?.configurator)
      return this._selectedModel?.configurator as SugarModelConfigurator;
    return this._configurators[
      this._configurators.length - 1
    ] as SugarModelConfigurator;
  }

  private fitCamera() {
    const newSettings = this.engine!.activeCamera?.fit(this);
    this._smoothOrbitControls.setOrbitTarget(newSettings!.target);
    this._smoothOrbitControls.setOrbitPosition(newSettings!.position);
  }

  private setMaterials(checkModel: Object3D) {
    this._mapMaterialObjects = fetchMaterialObjectMap(checkModel);

    checkModel?.traverse((o) => {
      if (o instanceof Mesh) {
        o.material = this._material;
        //o.material.color = new Color(0x0000ff);
      }
    });
  }

  private resetMaterials(checkModel: Object3D) {
    this._mapMaterialObjects?.keys()?.forEach((key) => {
      this._mapMaterialObjects?.get(key)?.forEach((object) => {
        (object as any).material = key;
        //(key as MeshStandardMaterial).color = new Color(0xffffff);
      });
    });
    this._mapMaterialObjects?.clear();
  }

  private __addModel(model: SugarModel) {
    if (!this._configurators.includes(model)) this._configurators.push(model);
    this.add(model);
  }

  private __removeModel(model: SugarModel) {
    this._configurators = this._configurators.filter((e) => e != model);
    this.remove(model);
  }

  onShowTopViewOccured(e: { detail: any }) {
    const camera = this.engine?.activeCamera?.instance as PerspectiveCamera;
    this._smoothOrbitControls.setOrbitPosition(
      this._orbitControls?.target
        ?.clone()
        .add(
          new Vector3(
            0,
            camera.position.distanceTo(this._orbitControls?.target!),
            0
          )
        )!,
      e?.detail
    );
    this._smoothOrbitControls.setOrbitTarget(
      this._orbitControls?.target!,
      e?.detail
    );
  }

  private _dimensionsController = new SugarModelDimensions();
  onShowDimensionsOccured(e: { detail: boolean }) {
    if (e?.detail == true) {
      this._dimensionsController.show(this.models);
    } else if (e?.detail == false) {
      this._dimensionsController.hide();
    } else {
      this._dimensionsController.toggle(this.models);
    }
  }

  onDeleteOccured() {
    this.removeSelected();
  }
}
